import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AdminSlideDrawer from '@sharedCR/common/AdminSlideDrawer';
import GuidelineFullView from 'app/pages/loanApplication/tabs/aiChat/components/GuidelineFullView';
import { aiProcessRunsSignalRController } from 'app/services/signalR';
import { initAiProcessRunsConnection } from 'app/services/signalR/aiProcessRunsConnector';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadRunDetailAction,
  selectProcessesUsedByOwner,
  selectRunById,
  selectStepGuidelineContent,
  selectStepGuidelineFullView,
  selectStepIsLoadingGuideline,
  selectStepsByRun,
  setStepGuidelineFullViewAction,
} from 'store/aiProcessRuns';

import { EXECUTION_GUIDELINE_FULL_VIEW_Z_INDEX } from '../AiActivity.constants';
import { AiActivityTestIds } from '../AiActivity.testIds';

import ExecutionDetailContent from './components/ExecutionDetailContent';

const ExecutionDetailDrawer = ({ executionId, ownerId, onClose }) => {
  const dispatch = useDispatch();
  const execution = useSelector(selectRunById(executionId));
  const steps = useSelector(selectStepsByRun(executionId));
  const processes = useSelector(selectProcessesUsedByOwner(ownerId));
  const guidelineFullViewCitation = useSelector(selectStepGuidelineFullView);
  const guidelineFullContent = useSelector(selectStepGuidelineContent);
  const isLoadingGuideline = useSelector(selectStepIsLoadingGuideline);

  const isOpen = !_.isNil(executionId);

  const onGuidelineFullViewClose = useCallback(() => {
    dispatch(setStepGuidelineFullViewAction(null));
  }, [dispatch]);

  const processesById = useMemo(
    () => Object.fromEntries((processes ?? []).map(item => [item.id, item])),
    [processes],
  );

  // Subscribe to the per-execution SignalR group BEFORE loading the baseline
  // detail. A freshly-started run auto-opens this drawer, and its first
  // step_*/execution_* events fire within the first seconds. The previous
  // implementation gated the join behind a loaded execution + live status, so
  // the subscription only attached after the one-time REST load resolved — by
  // which point the early events had already flown past unobserved and the open
  // drawer stayed frozen ("No steps yet" / duration stuck) for the whole run,
  // even though the loan-level group kept the Overview tiles live (DEV-3033).
  // Joining first, then reconciling with a fetch, lets live events merge on top
  // of the baseline via the upsert reducers so an open drawer ticks live.
  //
  // The join is chained off initAiProcessRunsConnection so the connection is
  // Connected before joinExecution runs on a cold load (mirrors how
  // AiActivityPanel sequences joinOwner). If the parent panel's init is still
  // in flight, joinExecution itself defers the id and resubscribe re-joins it.
  useEffectSafeStrictMode(() => {
    if (_.isNil(executionId)) {
      return undefined;
    }

    const cancellation = { cancelled: false };

    const subscribeThenLoad = async () => {
      await initAiProcessRunsConnection(dispatch).catch(() => null);

      if (cancellation.cancelled) {
        return;
      }

      // joinExecution swallows its own errors, so a failed subscribe still
      // falls through to the baseline load and the drawer is never left blank.
      await aiProcessRunsSignalRController.joinExecution(executionId).catch(() => null);

      if (!cancellation.cancelled) {
        dispatch(loadRunDetailAction({ executionId }));
      }
    };

    subscribeThenLoad();

    return () => {
      cancellation.cancelled = true;
      aiProcessRunsSignalRController.leaveExecution(executionId).catch(() => null);
    };
  }, [dispatch, executionId]);

  // Reset the guideline full-section drawer whenever the run drawer opens, closes,
  // or switches runs, so a section opened for one run never lingers into another.
  useEffectSafeStrictMode(() => {
    dispatch(setStepGuidelineFullViewAction(null));
  }, [dispatch, executionId]);

  return (
    <>
      <AdminSlideDrawer
        // The rich content header (icon + process name + Loan#/borrower subtitle)
        // is the single header and renders its own close control, so the drawer's
        // chrome header strip is suppressed to avoid a second, orphaned close X.
        hideHeader
        content={
          <ExecutionDetailContent
            execution={execution}
            processesById={processesById}
            steps={steps}
            testId={AiActivityTestIds.EXECUTION_DRAWER}
            onClose={onClose}
          />
        }
        open={isOpen}
        title=""
        onClose={onClose}
      />
      <GuidelineFullView
        isAskHidden
        citation={guidelineFullViewCitation}
        fullContent={guidelineFullContent}
        isLoading={isLoadingGuideline}
        isOpen={isOpen && !_.isNil(guidelineFullViewCitation)}
        zIndex={EXECUTION_GUIDELINE_FULL_VIEW_Z_INDEX}
        onClose={onGuidelineFullViewClose}
      />
    </>
  );
};

ExecutionDetailDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  executionId: PropTypes.string,
  ownerId: PropTypes.string,
};

export default memo(ExecutionDetailDrawer);
